import type { ReactNode } from "react";
import { hero, heroCard } from "@/data/showcase";
import { useReveal } from "@/lib/useReveal";

type StepState = "pending" | "approved" | "in-panel";

const STEPS: { n: string; time: string; title: string; sub: string; state: StepState }[] = [
  {
    n: "1",
    time: "12:59",
    title: "AI proponuje",
    sub: `Salon · gładź ścian · +${heroCard.deltaPct}% postępu`,
    state: "pending",
  },
  {
    n: "2",
    time: "13:14",
    title: "Koordynator zatwierdza",
    sub: "Spojrzenie, klik. Bez poprawek.",
    state: "approved",
  },
  {
    n: "✓",
    time: "13:14:01",
    title: "Klient widzi w panelu",
    sub: "Nowy procent. Te same fotki. W tej samej chwili.",
    state: "in-panel",
  },
];

export function Hero() {
  return (
    <section className="bg-ink-900 text-white py-16 md:py-24">
      <div className="mx-auto max-w-[1080px] px-6 grid md:grid-cols-12 gap-12 md:gap-12 lg:gap-16">
        <div className="min-w-0 md:col-span-5 lg:col-span-4 md:sticky md:top-12 self-start">
          <HeroCopy />
        </div>
        <div className="min-w-0 md:col-span-7 lg:col-span-8 relative">
          <Rail />
          <PauseComet />
          <Steps />
          <ProofBlock />
        </div>
      </div>
    </section>
  );
}

function HeroCopy() {
  return (
    <>
      <p className="text-[11px] font-mono uppercase tracking-[0.25em] text-ink-500 mb-6">
        {hero.eyebrow}
      </p>
      <h1 className="font-serif text-3xl md:text-4xl lg:text-5xl leading-[1.05] text-white mb-8">
        Foton zamienia zdjęcia z twoich kamer w gotową propozycję postępu prac.
      </h1>
      <p className="text-base text-ink-300 leading-relaxed max-w-[400px]">
        Codziennie rano. Koordynator klika{" "}
        <span className="text-foton-lighter">✓</span>, czasem coś poprawi. Klient widzi nowy
        procent w panelu w tej samej chwili.
      </p>
    </>
  );
}

function Rail() {
  return (
    <div className="absolute left-[12px] top-2 bottom-2 w-px bg-ink-700" />
  );
}

function PauseComet() {
  return (
    <span
      aria-hidden
      className="absolute"
      style={{
        left: "8px",
        width: "10px",
        height: "10px",
        borderRadius: "50%",
        background: "#BB97FF",
        boxShadow: "0 0 16px #8D51FF",
        animation: "lab-pause 6s ease-in-out infinite",
      }}
    />
  );
}

function Steps() {
  return (
    <ol className="relative">
      {STEPS.map((s, i) => (
        <RevealItem key={s.n} delayMs={i * 130}>
          <li className="pl-12 pb-12 last:pb-0 relative">
            <Station n={s.n} state={s.state} />
            <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-ink-500 mb-2">
              {s.time} · krok {i + 1}
            </p>
            <h3
              className={
                "font-serif text-2xl md:text-3xl leading-tight mb-2 " +
                (s.state === "approved" ? "text-foton-lighter" : "text-white")
              }
            >
              {s.title}
            </h3>
            <p className="text-sm md:text-base text-ink-400 leading-relaxed max-w-[440px]">
              {s.sub}
            </p>
          </li>
        </RevealItem>
      ))}
    </ol>
  );
}

function ProofBlock() {
  return (
    <div className="pl-12 mt-4 border-t border-ink-800 pt-10">
      <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink-500 mb-4">
        Inna kamera, inny etap · CAM-0004 · 19.05.2026
      </p>
      <div className="grid grid-cols-2 gap-2 mb-4 max-w-[520px]">
        <figure>
          <img
            src="/showcase/case-worker-before.jpg"
            alt="Przed"
            loading="lazy"
            className="w-full aspect-[4/3] object-cover"
          />
          <figcaption className="font-mono text-[10px] uppercase tracking-wider text-ink-600 mt-1.5">
            17.05 · before
          </figcaption>
        </figure>
        <figure>
          <img
            src="/showcase/case-worker-after.jpg"
            alt="Po"
            loading="lazy"
            className="w-full aspect-[4/3] object-cover"
          />
          <figcaption className="font-mono text-[10px] uppercase tracking-wider text-foton-lighter mt-1.5">
            19.05 · after
          </figcaption>
        </figure>
      </div>
      <div className="flex items-baseline justify-between max-w-[520px] border-t border-ink-800 pt-3">
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-500">
          Płytki w łazience · 30% → 42%
        </span>
        <span className="font-serif text-3xl md:text-4xl text-foton-light num leading-none">
          +12%
        </span>
      </div>
    </div>
  );
}

function Station({ n, state }: { n: string; state: StepState }) {
  return (
    <span
      className={
        "absolute -left-[1px] top-1 w-6 h-6 rounded-full text-xs font-mono flex items-center justify-center " +
        (state === "in-panel"
          ? "bg-ok-500 text-white"
          : "bg-ink-900 border-2 border-foton text-foton-lighter")
      }
    >
      {n}
    </span>
  );
}

function RevealItem({ children, delayMs = 0 }: { children: ReactNode; delayMs?: number }) {
  const ref = useReveal<HTMLDivElement>();
  return (
    <div ref={ref} className="reveal" style={{ transitionDelay: `${delayMs}ms` }}>
      {children}
    </div>
  );
}
